import { BaseContract } from '../common/base.contract';
import {Contract, ethers, formatEther, JsonRpcProvider, parseUnits} from 'ethers';
import { toNumber } from '../common/base-cdk-contract';
import StakingFacetAbi from '../../abi/Facets/StakingFacet.json';
import { 
    RoundStakingStatus, 
    StakingData,
    UserRound, 
    Stake 
} from '../../model/staking.model';
import { mapStakingData } from '../../mappers/staking.mapper';

// Classe principale
export class StakingFacet extends BaseContract {
    diamondAddress: string;
    constructor(address: string, provider: JsonRpcProvider) {
        super(new Contract(address, StakingFacetAbi.abi, provider));
        this.diamondAddress = address;
    }

    // Méthodes de lecture
    getStakingRoundDetails = async (stakingRoundId: string): Promise<StakingData> => {
        const data = await this.contract.getStakingRoundDetails(stakingRoundId);
        return mapStakingData(data);
    }

    getStakingStatus = async (stakingId: string): Promise<RoundStakingStatus> => {
        return await this.contract.getStakingStatus(stakingId);
    }

    getUserRounds = async (userAddress: string): Promise<UserRound[]> => {
        const rounds = await this.contract.getUserRounds(userAddress);
        return rounds.map((round: any) => ({
            saleRoundId: round.saleRoundId,
            stakingRoundId: round.stakingRoundId
        }));
    }

    getUserStakes = async (stakingId: string, userAddress: string): Promise<Stake[]> => {
        const stakes = await this.contract.getUserStakes(stakingId, userAddress);
        // TODO: check why we need to format the amount
        return stakes.map((stake: any) => ({
            amount: ethers.formatEther(stake.amount),
            createdAt: toNumber(stake.createdAt)
        }));
    }

    checkUserExists = async (stakingId: string, userAddress: string): Promise<boolean> => {
        return await this.contract.checkUserExists(stakingId, userAddress);
    }

    doCalculateRewards = async (stakingId: string, userAddress: string): Promise<number> => {
        const rewards = await this.contract.doCalculateRewards(stakingId, userAddress);
        return Number(ethers.formatEther(rewards.toString()).toString());
    }

    getWithdrawableAmount = async (stakingId: string, userAddress: string): Promise<number> => {
        const amount = await this.contract.getWithdrawableAmount(stakingId, userAddress);
        return Number(formatEther(amount.toString()));
    }

    // Méthodes d'écriture
    doStakeTokens = async (stakingId: string, amount: number) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doStakeTokens(stakingId, amount);
        await tx.wait();
    }

    doClaimRewards = async (stakingId: string) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doClaimRewards(stakingId);
        await tx.wait();
    }

    doCompoundRewards = async (stakingId: string) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doCompoundRewards(stakingId);
        await tx.wait();
    }

    doWithdrawTokens = async (stakingId: string, userAddress: string) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doWithdrawTokens(stakingId, userAddress);
        await tx.wait();
    }

    doWithdrawAndClaimRewards = async (stakingId: string) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doWithdrawAndClaimRewards(stakingId);
        await tx.wait();
    }

    doAddUserRoundIfNotExists = async (userAddress: string, saleRoundId: string, stakingRoundId: string) => {
        const contractWithSigner = await this._getSignedContract();
        const tx = await contractWithSigner.doAddUserRoundIfNotExists(userAddress, saleRoundId, stakingRoundId);
        await tx.wait();
    }
}
